import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import { createUuid } from '@notifi/uuid/browser';
import { ApiError } from '@/lib/api/errors';
import { toastApiError } from '@/lib/api/error-toast';
import { validationToastMessage } from '@/hooks/useApiMutation';
import {
  chargeRefusedBeforeCharging,
  classifyNewMethodChargeFailure,
  describeReplayedCharge,
  replayedEarlierMethod,
} from '@/lib/payments/newMethodCharge';
import type { CollectContactPaymentDto, StaffChargeAuthorization } from '@/types/contact';
import {
  useCollectContactPayment,
  useDeleteContactPaymentMethod,
} from './useContactPaymentMethods';

function formatUsd(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    Number.isFinite(amount) ? amount : 0,
  );
}

/**
 * How a new-method charge the server answered (without throwing) ended:
 * - `charged` — a card came back SUCCEEDED.
 * - `bank-submitted` — a bank debit came back PENDING, which is accepted and
 *   done from the operator's side (it clears in days, by design).
 * - `unconfirmed` — anything else, e.g. a CARD that came back PENDING. The
 *   charge may still land, so the dialog must stay open and locked.
 */
export type NewMethodChargeOutcome = 'charged' | 'bank-submitted' | 'unconfirmed';

export interface ContactNewMethodChargeOptions {
  /**
   * Shown when the server never definitively answered (lost response, 5xx,
   * 429, a still-processing or mismatched idempotency key). Names where the
   * operator should look before trying again.
   */
  ambiguousMessage: string;
  /** Toast for an `unconfirmed` outcome. */
  unconfirmedMessage: string;
  /** Prefix for the console error when removing a one-time method fails. */
  logLabel: string;
}

export interface ContactNewMethodChargeInput {
  /** What `NewPaymentMethodCharge` hands back through `onMethodReady`. */
  paymentMethodId: string;
  /** The saved method's provider-side id — what a replayed charge is told apart by. */
  providerPaymentMethodId?: string | null;
  removeAfterCharge: boolean;
  authorization?: StaffChargeAuthorization;
  /**
   * What `NewPaymentMethodCharge` computed the customer is debited before
   * any card fee (base after an ACH/check discount, plus the tip) — named in
   * the success copy. Falls back to `amount` + tip.
   */
  chargedAmount?: number;
  rail: 'card' | 'ach';
  /** Sent as the DTO's `amount` — the base, never including `tipAmount`. */
  amount: number;
  /**
   * Everything else the dialog owns on the DTO (`jobId`, `tipAmount`,
   * `autoApply`, `communicationPreference`, ...). The fields this hook
   * controls itself cannot be passed.
   */
  details?: Omit<
    CollectContactPaymentDto,
    | 'paymentMethodId'
    | 'amount'
    | 'removeAfterCharge'
    | 'newlyEnteredMethod'
    | 'achAuthorization'
    | 'idempotencyKey'
  >;
}

/**
 * Charges a card / bank account staff just entered in `NewPaymentMethodCharge`
 * through the contact-collect endpoint. Shared by every dialog that charges a
 * new method against a contact (the contact Collect dialog, the job Record
 * payment dialog) so the money rules live in one place:
 *
 * - The idempotency key is held across attempts for the same rail + amount +
 *   tip ONLY while the last outcome is unknown (network, 5xx, 429, a 409 that
 *   is not a key mismatch), so a retry replays that charge instead of running
 *   the card again. Success, a decline and a key mismatch all mint a fresh
 *   key next time.
 * - A failure is classified once: the ambiguous copy for an unknown outcome,
 *   the mapped decline message otherwise — exactly one toast either way.
 * - A method saved for one-time use comes off the contact only when it is
 *   known not to have been charged, best-effort, and never masks the real
 *   error: on a definitive refusal, or — after an ambiguous failure, when
 *   staff have entered another method under the held key — when the answer
 *   proves this one was not charged (a door refusal, a 409 for the earlier
 *   attempt, or a replay of the earlier attempt's charge on a method sent
 *   earlier under the same key).
 *   A method this dialog has sent before is never removed on such proof: the
 *   processor can hand the same stored method back for the same card, and
 *   the unresolved attempt may have charged it.
 * - The charge promise rejects on failure (the collector unlocks only on a
 *   rejection) and resolves with the outcome otherwise.
 *
 * `isCharging` re-renders the caller locked; `isChargingRef` is the same-tick
 * guard a submit handler reads (react state arrives a render late).
 */
export function useContactNewMethodCharge(
  contactId: string,
  { ambiguousMessage, unconfirmedMessage, logLabel }: ContactNewMethodChargeOptions,
) {
  // Its own mutation instance so the generic result/error toasts can be
  // suppressed without touching a caller's stored-method collect flow; this
  // hook shows exactly one message per outcome instead.
  const collectPayment = useCollectContactPayment(contactId, {
    showSuccessToast: false,
    showErrorToast: false,
  });
  // A hard-declined one-time method is already off the contact server-side.
  const deletePaymentMethod = useDeleteContactPaymentMethod(contactId, { notFoundIsRemoved: true });

  // `providerIds`: every method sent under `key`, so an answer that replays
  // one of them can be told from a charge of the method just sent.
  const attemptRef = useRef<{
    key: string;
    fingerprint: string;
    providerIds: ReadonlySet<string>;
  } | null>(null);
  // Every method this hook has sent a charge for. Kept across `reset`: a
  // method sent once may be the one an unresolved charge took.
  const sentMethodIdsRef = useRef(new Set<string>());
  const isChargingRef = useRef(false);
  const [isCharging, setIsCharging] = useState(false);
  const [unconfirmed, setUnconfirmed] = useState(false);

  /** Start a fresh dialog session: never replay a key minted for a previous one. */
  const reset = useCallback(() => {
    attemptRef.current = null;
    setUnconfirmed(false);
  }, []);

  async function removeOneTimeMethod(paymentMethodId: string, reason: string) {
    try {
      await deletePaymentMethod.mutateAsync(paymentMethodId);
    } catch (removalError) {
      console.error(
        `[${logLabel}] failed to remove a one-time payment method after ${reason}`,
        removalError,
      );
    }
  }

  async function charge({
    paymentMethodId,
    providerPaymentMethodId,
    removeAfterCharge,
    authorization,
    chargedAmount,
    rail,
    amount,
    details,
  }: ContactNewMethodChargeInput): Promise<NewMethodChargeOutcome> {
    const tipAmount = details?.tipAmount ?? 0;
    const fingerprint = JSON.stringify([rail, amount, tipAmount]);
    const previousAttempt = attemptRef.current;
    const reusesKey = !!previousAttempt && previousAttempt.fingerprint === fingerprint;
    const idempotencyKey = reusesKey ? previousAttempt.key : createUuid();
    const sentEarlierUnderKey: ReadonlySet<string> = reusesKey
      ? previousAttempt.providerIds
      : new Set();
    attemptRef.current = {
      key: idempotencyKey,
      fingerprint,
      providerIds: providerPaymentMethodId
        ? new Set([...sentEarlierUnderKey, providerPaymentMethodId])
        : sentEarlierUnderKey,
    };
    const sentBefore = sentMethodIdsRef.current.has(paymentMethodId);
    sentMethodIdsRef.current.add(paymentMethodId);
    isChargingRef.current = true;
    setIsCharging(true);

    try {
      const result = await collectPayment.mutateAsync({
        ...details,
        paymentMethodId,
        amount,
        removeAfterCharge: removeAfterCharge || undefined,
        // Scopes the server's bank-authorization requirement to this
        // collector: a bank account already saved on the contact is charged
        // without one.
        newlyEnteredMethod: true,
        achAuthorization: authorization,
        idempotencyKey,
      });

      // Definitive: never replay a charge the server answered.
      attemptRef.current = null;

      const status = typeof result?.status === 'string' ? result.status : undefined;
      const charged = chargedAmount ?? amount + tipAmount;
      // The held key named an earlier attempt, and this is its charge, on the
      // method entered then — not the one just entered, which was never sent
      // to the processor. Report that payment as it stands.
      if (
        !sentBefore &&
        replayedEarlierMethod(result, providerPaymentMethodId, sentEarlierUnderKey)
      ) {
        if (removeAfterCharge) {
          await removeOneTimeMethod(paymentMethodId, 'a replayed charge');
        }
        const replay = describeReplayedCharge(result, rail, charged, formatUsd);
        if (replay.settled) toast.success(replay.message);
        else toast.info(replay.message);
        // Never a PENDING row: the contact route answers a key whose payment
        // is still PENDING with a 409, so a replay is always a committed one.
        return 'charged';
      }
      // A bank debit is PENDING for days by design: accepted. A card that
      // comes back PENDING is not.
      if (rail === 'ach' && status === 'PENDING') {
        toast.success(
          `Bank payment of ${formatUsd(charged)} submitted — clears in 3–5 business days.`,
        );
        return 'bank-submitted';
      }
      if (status === 'SUCCEEDED') {
        toast.success(`Payment of ${formatUsd(charged)} charged.`);
        return 'charged';
      }
      setUnconfirmed(true);
      toast.info(unconfirmedMessage);
      return 'unconfirmed';
    } catch (error) {
      const { canRetryWithSameKey, isAmbiguousOutcome } = classifyNewMethodChargeFailure(error);
      if (!canRetryWithSameKey) {
        attemptRef.current = null;
      }

      if (isAmbiguousOutcome) {
        toast.error(ambiguousMessage);
      } else if (error instanceof ApiError) {
        // Mirrors `useApiMutation`'s own built-in error toast, which this
        // mutation suppresses so it can't stack with the ambiguous copy.
        const fieldErrors = error.fieldErrors;
        if (fieldErrors && fieldErrors.length > 0) {
          toastApiError(error, validationToastMessage(fieldErrors));
        } else if (error.code !== 'CANCELED') {
          toastApiError(error);
        }
      }

      // Never on an ambiguous outcome for THIS method: the charge may have
      // gone through, and removing the method would strand it from the charge
      // it made. An ambiguous answer about an EARLIER attempt that proves this
      // request charged nothing is not that.
      const provenNotCharged = !sentBefore && chargeRefusedBeforeCharging(error);
      if (removeAfterCharge && (!isAmbiguousOutcome || provenNotCharged)) {
        await removeOneTimeMethod(paymentMethodId, 'a refused charge');
      }
      throw error;
    } finally {
      isChargingRef.current = false;
      setIsCharging(false);
    }
  }

  return { charge, reset, isCharging, isChargingRef, unconfirmed };
}
